import { useEffect, useRef, useState, type CSSProperties, type ReactElement } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { getView, setView } from "../lib/viewState";

export interface CarouselSlide {
  key: string;
  label: string;
  hue?: string;
  render: () => ReactElement;
}

const hueStyle = (hue?: string) => ({ "--seg-hue": hue }) as CSSProperties;

export function Carousel({
  param,
  slides,
  defaultKey,
}: {
  param: "cview" | "lview";
  slides: CarouselSlide[];
  defaultKey: string;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get(param);
  // Effective selection: explicit URL param wins, else the persisted choice (held across pages),
  // else the section default.
  const persisted = getView(param);
  const activeKey = slides.some((s) => s.key === raw)
    ? (raw as string)
    : persisted && slides.some((s) => s.key === persisted)
      ? persisted
      : defaultKey;
  const idx = Math.max(0, slides.findIndex((s) => s.key === activeKey));
  const active = slides[idx];

  // Persist whatever slide is currently shown so it survives navigation.
  useEffect(() => {
    setView(param, activeKey);
  }, [param, activeKey]);

  const prevIdxRef = useRef(idx);
  const [dir, setDir] = useState(0);
  useEffect(() => {
    setDir(Math.sign(idx - prevIdxRef.current));
    prevIdxRef.current = idx;
  }, [idx]);

  // Deep-link scroll: when the named search param drives a slide change, bring
  // the enclosing section up under the 64px sticky bar — instantly on first
  // paint (arriving via a deep link), smoothly afterwards.
  const rootRef = useRef<HTMLDivElement>(null);
  const enabled = raw != null;
  const firstRef = useRef(true);
  useEffect(() => {
    if (!enabled) {
      firstRef.current = false;
      return;
    }
    const root = rootRef.current;
    const el = root ? root.closest("section") ?? root : null;
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - 64;
    window.scrollTo({ top, behavior: firstRef.current ? "auto" : "smooth" });
    firstRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey]);

  const select = (key: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set(param, key);
      return next;
    });
  };

  const atStart = idx <= 0;
  const atEnd = idx >= slides.length - 1;
  const prev = atStart ? null : slides[idx - 1];
  const next = atEnd ? null : slides[idx + 1];
  const step = (delta: number) => {
    const t = idx + delta;
    if (t < 0 || t > slides.length - 1) return; // stop at the ends — no wrap
    select(slides[t].key);
  };

  return (
    <div className="carousel-body" data-c="Carousel" ref={rootRef}>
      <div className="carousel-tabs" role="tablist" style={{ justifyContent: "center" }}>
        {slides.map((s) => (
          <button
            key={s.key}
            type="button"
            role="tab"
            aria-selected={s.key === activeKey}
            style={hueStyle(s.hue)}
            className={`carousel-tab ${s.key === activeKey ? "active" : ""}`}
            onClick={() => select(s.key)}
          >
            <span>{s.label}</span>
          </button>
        ))}
      </div>

      <div className="carousel-stage">
        {prev && (
          <div className="carousel-edge is-left">
            <button
              type="button"
              style={hueStyle(prev.hue)}
              aria-label={`Previous: ${prev.label}`}
              onClick={() => step(-1)}
            >
              <ChevronLeft size={22} />
              <span className="carousel-edge-peek">{prev.label}</span>
            </button>
          </div>
        )}
        {next && (
          <div className="carousel-edge is-right">
            <button
              type="button"
              style={hueStyle(next.hue)}
              aria-label={`Next: ${next.label}`}
              onClick={() => step(1)}
            >
              <span className="carousel-edge-peek">{next.label}</span>
              <ChevronRight size={22} />
            </button>
          </div>
        )}
        <div
          key={active.key}
          className={`carousel-slide ${dir > 0 ? "from-right" : dir < 0 ? "from-left" : ""}`}
        >
          {active.render()}
        </div>
      </div>

      <div className="carousel-foot">
        <div className="carousel-dots" aria-hidden="true">
          {slides.map((s, i) => (
            <button
              type="button"
              key={s.key}
              className={`carousel-dot ${i === idx ? "on" : ""}`}
              style={hueStyle(s.hue)}
              aria-label={s.label}
              onClick={() => select(s.key)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
